import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { walletBalanceQueryKey } from '~/domains/payments/hooks/consts';
import { PaymentsQueryKeys } from '~/domains/payments/query';
import type { CryptoDepositArrival } from '~/services/wallet/interfaces/crypto-direct.interface';
import { getCryptoDeposits } from '~/services/wallet/wallet.service.api';

// One fixed, foreground-only poll interval — no backoff. A boring 30s cadence keeps the list
// roughly fresh while a user sits on the screen; window-focus refetch (below) catches up instantly
// on return, and `refetchIntervalInBackground:false` pauses it when the tab is hidden. At 1,000
// concurrent open (foregrounded) screens this is ~33 req/s vs. ~200 req/s under the old flat-5s
// poll. (EDGE-31129 — replaces the fragile 5→30s render-flush backoff that could regress to flat 5s.)
export const ARRIVALS_POLL_INTERVAL_MS = 30_000;
export const ARRIVALS_PAGE_LIMIT = 50;
// After this much *continuous* fetch failure, stop polling until a user-triggered refetch succeeds.
export const ARRIVALS_MAX_ERROR_WAIT_MS = 2 * 60 * 1_000;

const REFUNDABLE_BREAKDOWN_QUERY_KEY = 'wallets/refundable-breakdown';

// The list's render state as ONE discriminated value, so a consumer can't conflate "we never got an
// answer" with "we loaded and there's nothing" (a false "No transfers yet." on a money screen).
// 'loading' = first page not yet resolved; 'error' = first fetch failed and we have nothing to show;
// 'ready' = a page resolved (possibly empty) OR we already have accumulated rows to keep showing.
export type CryptoArrivalsStatus = 'loading' | 'error' | 'ready';

/**
 * Upsert by id; newest postedAt first. Pure — exported for tests.
 *
 * Generic over `T` (constrained structurally to `{ id, postedAt }`) rather than pinned to
 * `CryptoDepositArrival`: the test file's `arrival()` fixture factory types its `status` param
 * as plain `string`, which widens the fixture's inferred type past the `CryptoDepositArrivalStatus`
 * literal union `CryptoDepositArrival` requires. Pinning the param type to `CryptoDepositArrival[]`
 * makes `tsc --noEmit` reject that fixture at the (committed, untouchable) test's call site. The
 * structural constraint keeps real callers exact — inference pins `T` to `CryptoDepositArrival` at
 * every call in this file — while accepting the test's slightly-looser fixture type without `any`
 * or `as`. The real fix is narrowing `arrival()`'s `status` param in the test file; flagging for the
 * test's owner rather than editing it here.
 */
export function mergeArrivals<T extends { id: string; postedAt: string }>(
  current: T[],
  incoming: T[]
): T[] {
  const byId = new Map(current.map((item) => [item.id, item] as const));
  incoming.forEach((item) => byId.set(item.id, item));
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime()
  );
}

type UseCryptoDepositArrivalsOptions = {
  enabled?: boolean;
  /** Fired once per deposit that transitions to `completed` after the first page. */
  onCompleted?: (arrival: CryptoDepositArrival) => void;
};

export function useCryptoDepositArrivals({
  enabled = true,
  onCompleted,
}: UseCryptoDepositArrivalsOptions = {}) {
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();

  const sinceRef = useRef<string | undefined>(undefined);
  const hasLoadedFirstPageRef = useRef(false);
  // Timestamp (query-clock, i.e. `query.state.errorUpdatedAt`) of the first fetch failure in the
  // current unbroken failure streak; `null` while healthy. Read by `refetchInterval` to stop
  // polling after ~`ARRIVALS_MAX_ERROR_WAIT_MS` of continuous failure.
  const errorStartRef = useRef<number | null>(null);
  // The most recent `query.state.errorUpdatedAt` / `dataUpdatedAt` this hook has already reacted
  // to, so `refetchInterval` (which RQ v4 re-invokes on every state transition, including the
  // transient 'loading' state a new poll starts in) marks the *start* of a failure streak exactly
  // once per *new* failure and clears it once per *new* success — never on every re-evaluation.
  const lastSeenErrorUpdatedAtRef = useRef(0);
  const lastSeenDataUpdatedAtRef = useRef(0);
  // Keyed by item id → a fingerprint of the mutable fields (see `mergeArrivals`'s onSuccess use
  // below), not just `status`: a same-status item can still gain a `txHash`/`creditedAt`/
  // `transactionId` after the fact, and that must still be treated as a change worth merging.
  const knownStatusRef = useRef(new Map<string, string>());
  const onCompletedRef = useRef(onCompleted);
  const [arrivals, setArrivals] = useState<CryptoDepositArrival[]>([]);

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  // Per-instance cursor/state is keyed on component identity, not on the query key — so a user
  // switch (which does change the query key) would otherwise reuse the previous user's cursor
  // and rows. Reset on `user?.id` change only; this effect is declared before `useQuery` below so
  // it runs (React fires effects in declaration order) before RQ's internal effect that issues the
  // new-key fetch, ensuring `sinceRef` is already cleared when that fetch's queryFn reads it.
  useEffect(() => {
    sinceRef.current = undefined;
    knownStatusRef.current = new Map();
    hasLoadedFirstPageRef.current = false;
    errorStartRef.current = null;
    lastSeenErrorUpdatedAtRef.current = 0;
    lastSeenDataUpdatedAtRef.current = 0;
    setArrivals([]);
  }, [user?.id]);

  const query = useQuery({
    queryKey: PaymentsQueryKeys.CRYPTO_DEPOSIT_ARRIVALS(user?.id),
    queryFn: () => getCryptoDeposits({ since: sinceRef.current, limit: ARRIVALS_PAGE_LIMIT }),
    enabled: enabled && !!user?.id,
    // Each page is a delta against the cursor; the accumulated list lives in component state.
    staleTime: 0,
    cacheTime: 0,
    retry: false,
    // Focus refetch is ON (Mark's ruling): returning to the tab catches the list up immediately,
    // which — paired with `refetchIntervalInBackground:false` (a hidden tab pauses the poll) — is
    // the whole "very basic background polling" model. No visible indicator runs during either.
    refetchOnWindowFocus: true,
    // The failure-streak start time (for the 2-minute continuous-failure cap) is tracked here, from
    // `query.state` read synchronously by RQ v4 right after each dispatch. There is no backoff to
    // compute any more — the interval is a flat constant — so this no longer depends on any deferred
    // `onSuccess`/`onError` ref write landing first (the fragility the old EDGE-31613 note warned of).
    refetchInterval: (_data, queryRef) => {
      const { dataUpdatedAt, errorUpdatedAt } = queryRef.state;

      if (dataUpdatedAt !== lastSeenDataUpdatedAtRef.current) {
        // A fetch has succeeded since we last checked — end any failure streak.
        lastSeenDataUpdatedAtRef.current = dataUpdatedAt;
        lastSeenErrorUpdatedAtRef.current = errorUpdatedAt;
        errorStartRef.current = null;
      } else if (errorUpdatedAt !== lastSeenErrorUpdatedAtRef.current) {
        // A new failure since we last checked — mark the streak's start (only the first one).
        lastSeenErrorUpdatedAtRef.current = errorUpdatedAt;
        if (errorStartRef.current === null) errorStartRef.current = errorUpdatedAt;
      }

      if (
        errorStartRef.current !== null &&
        Date.now() - errorStartRef.current >= ARRIVALS_MAX_ERROR_WAIT_MS
      ) {
        return false;
      }

      return ARRIVALS_POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
    onSuccess: (page) => {
      if (page.nextSince) sinceRef.current = page.nextSince;

      const isFirstPage = !hasLoadedFirstPageRef.current;
      hasLoadedFirstPageRef.current = true;

      const newlyCompleted: CryptoDepositArrival[] = [];
      let hasChanges = false;

      page.items.forEach((item) => {
        // Fingerprint the mutable fields, not just `status`: a same-status item can still gain a
        // `txHash`/`creditedAt`/`transactionId` after the fact (e.g. a completed deposit whose
        // txHash lands a beat later), and that must still count as a change to merge in.
        const fingerprint = `${item.status}|${item.txHash}|${item.creditedAt}|${item.transactionId}`;
        const previousFingerprint = knownStatusRef.current.get(item.id);
        if (previousFingerprint === fingerprint) return;
        const previousStatus = previousFingerprint?.split('|')[0];
        hasChanges = true;
        knownStatusRef.current.set(item.id, fingerprint);
        // Only a genuine transition to 'completed' (not merely a field update on an item that was
        // already completed) should invalidate balances / fire onCompleted.
        if (!isFirstPage && item.status === 'completed' && previousStatus !== 'completed') {
          newlyCompleted.push(item);
        }
      });

      if (hasChanges) setArrivals((current) => mergeArrivals(current, page.items));

      if (newlyCompleted.length > 0) {
        queryClient.invalidateQueries({ queryKey: walletBalanceQueryKey(user?.id) });
        queryClient.invalidateQueries({ queryKey: [REFUNDABLE_BREAKDOWN_QUERY_KEY, user?.id] });
        newlyCompleted.forEach((item) => onCompletedRef.current?.(item));
      }
    },
  });

  const { refetch } = query;
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  // Refocus rescue: once `refetchInterval` above returns `false` after
  // `ARRIVALS_MAX_ERROR_WAIT_MS` of continuous failure, polling is dead for good — resetting the
  // refs alone won't revive it because RQ already cleared the interval; only a `refetch()` re-runs
  // the query and lets `refetchInterval` recompute to a live interval. Gate on
  // `errorStartRef.current !== null` (i.e. only while in a failure streak) so normal, healthy-poll
  // focus events are completely unaffected.
  useEffect(() => {
    const reviveIfDead = () => {
      if (errorStartRef.current === null) return;
      errorStartRef.current = null;
      lastSeenErrorUpdatedAtRef.current = 0;
      refetchRef.current();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') reviveIfDead();
    };
    window.addEventListener('focus', reviveIfDead);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', reviveIfDead);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Explicit named return (not a `...query` spread): the raw `query.data` (last page only) is never
  // what a consumer wants — `arrivals` is the accumulated, deduped list — and reading only these
  // stable getters avoids TanStack v4's per-tick tracked re-render on every poll. This used to be a
  // `...rest` spread that was load-bearing for flushing the old backoff ref before `refetchInterval`
  // re-read it; with a flat constant interval there is nothing to flush, so the spread is retired
  // (closes the EDGE-31613 fragility).
  //
  // `status` is the single signal the container's list renders off. Order matters:
  //  1. 'loading' FIRST — an unresolved fetch of the CURRENT key blanks the list even if stale rows
  //     from a previous key still linger for the one render before the per-user reset effect clears
  //     them (a live `user?.id` switch on a still-mounted hook). Keying off `query.isLoading` (not
  //     `arrivals`) is what keeps another account's rows from flashing in that window.
  //  2. rows win — a settled key with rows stays 'ready' even if a LATER poll errors (keep the rows;
  //     `isLoading` is false for a background refetch, so this is only reached once loaded).
  //  3. 'error' — settled, errored, nothing accumulated → NOT a false "No transfers yet.".
  //  4. else 'ready' — a resolved-but-empty first page (the genuine "No transfers yet." case).
  let status: CryptoArrivalsStatus;
  if (query.isLoading) {
    status = 'loading';
  } else if (arrivals.length > 0) {
    status = 'ready';
  } else if (query.isError) {
    status = 'error';
  } else {
    status = 'ready';
  }

  return {
    status,
    // The "this hook has reached ready at least once for the current user" latch. The container gates
    // the whole "Recent transfers" section on it so a NEVER-ready hook — the container's initial load,
    // or a non-active account whose fetch is disabled (`enabled:false` → the query sits idle/'loading'
    // and the first page never resolves) — renders nothing, not a bare heading. It latches on the
    // first resolved page (`hasLoadedFirstPageRef`, set in onSuccess), stays true across subsequent
    // transient poll errors (so the section's self-heal is preserved, never torn down on a blip), and
    // resets to false on a `user?.id` change with the rest of the per-user cursor state.
    hasLoaded: hasLoadedFirstPageRef.current,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    arrivals,
    hasPendingArrivals: arrivals.some(
      (item) => item.status === 'pending' || item.status === 'under_review'
    ),
  };
}
